import type {
  InvestorsResponse,
  NasdaqPrivateMarketCompany,
  NasdaqPrivateMarketListResponse,
  NasdaqPrivateMarketPricingDate,
  NasdaqPrivateMarketPricingPoint,
  NasdaqPrivateMarketPricingResponse,
  NormalizedPrivateMarketPrice,
  PrivateCompanyCapitalRaise,
  PrivateCompanyInvestor,
  PrivateCompanyPerson,
  PrivateCompanyStat,
} from './entities';

function asObjectArray<T extends object>(value: unknown): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => Boolean(item) && typeof item === 'object');
}

function formatStatValue(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (Number.isInteger(value) && Math.abs(value) < 10_000) return String(value);
    return Number.isInteger(value)
      ? value.toLocaleString('en-US')
      : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  if (typeof value === 'string' && value.trim()) return value.trim();
  return undefined;
}

function formatMoney(value: number): string {
  if (value >= 1_000_000_000)
    return `$${(value / 1_000_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`;
  if (value >= 1_000) return `$${(value / 1_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}K`;
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function formatInvestmentDate(date: PrivateCompanyCapitalRaise['investmentDate']): string | undefined {
  if (!date || typeof date !== 'object') return undefined;
  const { day, month, year } = date;
  if (!year || !month || !day) return undefined;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatSharePrice(value: number): string {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function pricingTimestamp(date: NasdaqPrivateMarketPricingDate | undefined): number | null {
  if (!date?.year || !date?.month || !date?.day) return null;
  return Date.UTC(date.year, date.month - 1, date.day);
}

export function getNormalizedPricing(
  pricing: NasdaqPrivateMarketPricingResponse | NasdaqPrivateMarketListResponse | null | undefined,
): NormalizedPrivateMarketPrice[] {
  const rows = asObjectArray<NasdaqPrivateMarketPricingPoint>(pricing?.data);
  return rows
    .map(row => {
      const price = typeof row.price === 'number' && Number.isFinite(row.price) ? row.price : null;
      const timestamp = pricingTimestamp(row.dateOfPricing);
      if (price == null || timestamp == null) return null;
      return {
        date: formatInvestmentDate(row.dateOfPricing) ?? '',
        price,
        timestamp,
      };
    })
    .filter((row): row is NormalizedPrivateMarketPrice => Boolean(row))
    .sort((left, right) => left.timestamp - right.timestamp);
}

export function getInvestorsCompanyName(payload: InvestorsResponse | null | undefined, fallbackSymbol: string): string {
  if (!payload) return fallbackSymbol;
  const companyName = payload.company?.name || payload.company?.doingBusinessAs;
  return typeof companyName === 'string' && companyName.trim() ? companyName.trim() : fallbackSymbol;
}

export function getInvestorsList(payload: InvestorsResponse | null | undefined): PrivateCompanyInvestor[] {
  if (!payload) return [];
  return asObjectArray<PrivateCompanyInvestor>(payload.investors);
}

export function getCapitalRaises(payload: InvestorsResponse | null | undefined): PrivateCompanyCapitalRaise[] {
  if (!payload) return [];
  return asObjectArray<PrivateCompanyCapitalRaise>(payload.capital_raises).map(row => {
    const amountNumber = typeof row.investmentAmount === 'number' ? row.investmentAmount : undefined;
    return {
      ...row,
      amount: amountNumber != null ? formatMoney(amountNumber) : '—',
      date: formatInvestmentDate(row.investmentDate) ?? '—',
      series: row.financingRound?.trim() || '—',
    };
  });
}

export function getKeyPeople(payload: InvestorsResponse | null | undefined): PrivateCompanyPerson[] {
  if (!payload) return [];
  return asObjectArray<PrivateCompanyPerson>(payload.people).map(row => {
    const fullName = [row.firstName, row.lastName]
      .filter((part): part is string => typeof part === 'string' && Boolean(part.trim()))
      .join(' ');
    return {
      ...row,
      name: fullName || '—',
      title: row.title?.trim() || '—',
    };
  });
}

export function getPrivateCompanyStats(payload: InvestorsResponse | null | undefined): PrivateCompanyStat[] {
  if (!payload) return [];

  const company = payload.company;
  const raises = getCapitalRaises(payload);
  const totalRaised = raises.reduce((sum, row) => {
    const amount = typeof row.investmentAmount === 'number' ? row.investmentAmount : 0;
    return sum + amount;
  }, 0);

  const priceValue =
    typeof payload.lastPrice === 'number' ? formatSharePrice(payload.lastPrice) : formatStatValue(payload.price);

  const optionalStats: Array<[string, unknown]> = [
    ['Amount Raised', totalRaised > 0 ? formatMoney(totalRaised) : undefined],
    ['Founded', company?.yearFounded],
    ['Headquarters', company?.headquarters],
    ['Sector', company?.sector],
    ['Exchange', 'Nasdaq Private Market'],
  ];

  return [
    { label: 'Price', value: priceValue ?? '—' },
    { label: 'Valuation', value: '—' },
    ...optionalStats
      .map(([label, value]) => {
        const formatted = formatStatValue(value);
        return formatted ? { label, value: formatted } : null;
      })
      .filter((stat): stat is PrivateCompanyStat => Boolean(stat)),
  ];
}

export function buildPrivateCompanyPayload(args: {
  companies?: NasdaqPrivateMarketListResponse | null;
  funding?: NasdaqPrivateMarketListResponse | null;
  investors?: InvestorsResponse | NasdaqPrivateMarketListResponse | null;
  management?: NasdaqPrivateMarketListResponse | null;
  pricing?: NasdaqPrivateMarketPricingResponse | NasdaqPrivateMarketListResponse | null;
  symbol: string;
}): InvestorsResponse {
  const company = asObjectArray<NasdaqPrivateMarketCompany>(args.companies?.data)[0] ?? null;
  const investorRows = asObjectArray<PrivateCompanyInvestor>(args.investors?.data);
  const fundingRows = asObjectArray<PrivateCompanyCapitalRaise>(args.funding?.data);
  const peopleRows = asObjectArray<PrivateCompanyPerson>(args.management?.data);
  const pricing = getNormalizedPricing(args.pricing);
  const latest = pricing[pricing.length - 1];
  const previous = pricing.length > 1 ? pricing[pricing.length - 2] : undefined;

  return {
    capital_raises: fundingRows,
    company,
    description: company?.description,
    investors: investorRows,
    lastPrice: latest?.price,
    people: peopleRows,
    previousPrice: previous?.price,
    price: latest ? formatSharePrice(latest.price) : undefined,
    pricing,
    symbol: args.symbol,
  };
}
